import { AvatarWithFlag } from 'components/AvatarWithFlag';
import './ProfileMenu.css';
import { AnimatedPoints } from 'components/Points/AnimatedPoints';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Skeleton, useTheme } from '@mui/material';
import { useGetMeQuery } from 'store/services/user/meApi';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { logout } from 'store/services/auth/authSlice';
import { meApi } from 'store/services/user/meApi';
import { haveListApi } from 'store/services/collectionList/haveList/haveListApi';
import { wantListApi } from 'store/services/collectionList/wantList/wantListApi';
import { tradeApi } from 'store/services/trade/tradeApi';
import { cardsSearchApi } from 'store/services/cards/search/cardsSearchApi';
import { authApi } from 'store/services/auth/authApi';
import { AssetUrls } from 'config/assets';
import EliteUpsellBanner from 'components/EliteUpsellBanner/EliteUpsellBanner';
import { Points } from 'components/Points/Points';
import { useFinishSetup } from 'hooks/useFinishSetup';
// One icon source for the menu rows (Asana 1219037291448908): per-icon MUI
// imports, tree-shaken, the way InfoPopover and TradeMapShareModal import theirs.
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';

export const ProfileMenu = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth?.token);
  const user = useSelector((state: RootState) => state.auth?.user);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch user data if we have a token OR user (cookie mode)
  const { isLoading, data } = useGetMeQuery(undefined, {
    skip: !token && !user, // Skip only if neither token nor user exists
    refetchOnFocus: true,  // Refresh user data when returning to tab
  });

  // "Finish setup" (Asana 1218957945501621): while the Setup Banner is hidden
  // and rewards remain, the way back to them lives here.
  const finishSetup = useFinishSetup(!!token || !!user);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleLogout = () => {
    // Clear all RTK Query caches to prevent data leakage between accounts
    dispatch(meApi.util.resetApiState());
    dispatch(haveListApi.util.resetApiState());
    dispatch(wantListApi.util.resetApiState());
    dispatch(tradeApi.util.resetApiState());
    dispatch(cardsSearchApi.util.resetApiState());
    dispatch(authApi.util.resetApiState());

    // Clear auth token
    dispatch(logout());

    // Navigate to welcome page
    navigate('/welcome');
  };

  return (
    <div className="profile-menu-container" ref={dropdownRef}>
      <div
        className="profile-menu"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        style={{ cursor: 'pointer' }}
      >
        <AvatarWithFlag
          isLoading={isLoading}
          avatarUrl={data?.avatarUrl}
          username={data?.username}
          countryCode={data?.country}
          size={37}
          isElite={data?.isElite}
          showAnimatedBorder={data?.showAnimatedBorder}
          borderColor1={data?.borderColor1}
          borderColor2={data?.borderColor2}
        />
        <div className="profile-menu-account-details">
          {isLoading ? (
            <Skeleton
              variant="rounded"
              sx={{ backgroundColor: theme.colors.grey, borderRadius: 12 }}
              width={68}
              height={20}
            />
          ) : (
            <div className="profile-menu-account-details-username">
              {data?.username ?? ''}
              {data?.isElite && (
                <img
                  src={AssetUrls.icons.elite.badge}
                  alt="Elite"
                  className="profile-menu-elite-badge"
                  title="TradeMagic Elite"
                />
              )}
            </div>
          )}
          <AnimatedPoints points={data?.points} isLoading={isLoading} />
        </div>
        <div className="profile-menu-arrow-menu">
          <FontAwesomeIcon
            icon={'chevron-down'}
            width={10}
            color={theme.colors.lighterGrey}
            style={{
              transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}
          />
        </div>
      </div>

      {isDropdownOpen && (
        <div className="profile-menu-dropdown">
          {data?.username && (
            <Link
              to={`/profile/u/${encodeURIComponent(data.username)}`}
              className="profile-menu-dropdown-item"
              onClick={() => setIsDropdownOpen(false)}
            >
              <PersonIcon className="profile-menu-dropdown-icon" aria-hidden="true" />
              <span>Profile</span>
            </Link>
          )}
          <Link
            to="/settings"
            className="profile-menu-dropdown-item"
            onClick={() => setIsDropdownOpen(false)}
          >
            <SettingsIcon className="profile-menu-dropdown-icon" aria-hidden="true" />
            <span>Settings</span>
          </Link>
          {finishSetup.visible && (
            <button
              type="button"
              className="profile-menu-dropdown-item profile-menu-dropdown-item--finish-setup"
              data-testid="finish-setup-item"
              disabled={finishSetup.busy}
              onClick={() => {
                setIsDropdownOpen(false);
                finishSetup.finishSetup();
              }}
            >
              <span className="profile-menu-dropdown-item-lead">
                <ChecklistIcon className="profile-menu-dropdown-icon" aria-hidden="true" />
                <span>Finish setup</span>
              </span>
              <span className="profile-menu-finish-setup-points">
                <Points type="inline" points={finishSetup.remainingPoints} /> left
              </span>
            </button>
          )}
          <Link
            to="/transaction-history"
            className="profile-menu-dropdown-item"
            onClick={() => setIsDropdownOpen(false)}
          >
            <ReceiptLongIcon className="profile-menu-dropdown-icon" aria-hidden="true" />
            <span>Ledger</span>
          </Link>
          {/* Enrolled (active-status) affiliates only — non-affiliates never
              see this; Settings > Affiliates stays the program's discovery path. */}
          {data?.isAffiliate === true && (
            <Link
              to="/affiliates/dashboard"
              className="profile-menu-dropdown-item"
              onClick={() => setIsDropdownOpen(false)}
            >
              <Diversity3Icon className="profile-menu-dropdown-icon" aria-hidden="true" />
              <span>Partners</span>
            </Link>
          )}
          <Link
            to="/faq"
            className="profile-menu-dropdown-item"
            onClick={() => setIsDropdownOpen(false)}
          >
            <HelpOutlineIcon className="profile-menu-dropdown-icon" aria-hidden="true" />
            <span>FAQ</span>
          </Link>
          {/* "Sign out", pairing with the sign-in tab's verb */}
          <div
            className="profile-menu-dropdown-item"
            role="button"
            tabIndex={0}
            data-testid="sign-out-item"
            onClick={handleLogout}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleLogout();
              }
            }}
          >
            <LogoutIcon className="profile-menu-dropdown-icon" aria-hidden="true" />
            <span>Sign out</span>
          </div>
          {!isLoading && data && !data.isElite && (
            <EliteUpsellBanner
              variant="dropdown"
              refSource="header_dropdown"
              onNavigate={() => setIsDropdownOpen(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};
